import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    Link,
    OutlinedInput,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { Link as RouterLink } from "react-router-dom";
import LogoUlo from "../../../components/LogoUlo/LogoUlo";
import { LoginFormValues } from "../loginSchema";

interface LoginFormProps {
    countdownLabel: string;
    form: UseFormReturn<LoginFormValues>;
    isLocked: boolean;
    onSubmit: (values: LoginFormValues) => void;
    saveEmail: boolean;
    setSaveEmail: React.Dispatch<React.SetStateAction<boolean>>;
    showPassword: boolean;
    togglePasswordVisibility: () => void;
}

const LoginForm = ({
    countdownLabel,
    form,
    isLocked,
    onSubmit,
    saveEmail,
    setSaveEmail,
    showPassword,
    togglePasswordVisibility,
}: LoginFormProps) => {
    const theme = useTheme();
    const isTabletScreen = useMediaQuery(theme.breakpoints.down("md"));
    const isLaptopScreen = useMediaQuery(theme.breakpoints.up("md"));

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = form;

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            paddingY={3}
            paddingX={isTabletScreen ? 2 : 4}
            sx={{
                backgroundColor: "#fff",
                borderRadius: "inherit",
            }}
        >
            <Link href="https://ukmlokal.com/" rel="noreferrer" target="_blank">
                <LogoUlo width="100px" />
            </Link>
            {isLaptopScreen ? (
                <Typography
                    marginTop={2}
                    component="div"
                    textAlign="center"
                    variant="h6"
                    fontWeight="bold"
                >
                    Tumbuh bersama UKM{" "}
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{ color: "#20965F" }}
                        display="inline"
                    >
                        LOKAL
                    </Typography>
                </Typography>
            ) : (
                <React.Fragment>
                    <Typography
                        marginTop={2}
                        textAlign="center"
                        variant="h6"
                        fontWeight="bold"
                    >
                        Tumbuh bersama
                    </Typography>
                    <Typography
                        component="div"
                        textAlign="center"
                        variant="h6"
                        fontWeight="bold"
                    >
                        UKM{" "}
                        <Typography
                            variant="h6"
                            fontWeight="bold"
                            sx={{ color: "#20965F" }}
                            display="inline"
                        >
                            LOKAL
                        </Typography>
                    </Typography>
                </React.Fragment>
            )}

            <form
                style={{
                    width: "100%",
                    maxWidth: "500px",
                    minWidth: "200px",
                }}
                onSubmit={handleSubmit(onSubmit)}
            >
                <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="#000"
                    fontSize="16px"
                    sx={{
                        width: "fit-content",
                        marginTop: 3,
                        borderBottom: "1px solid #20965F",
                        marginBottom: 2,
                    }}
                >
                    Masuk
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <Typography variant="body2">
                            Email atau Username
                        </Typography>
                        <Controller
                            name="email"
                            control={control}
                            render={({ field }) => (
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    error={Boolean(errors.email)}
                                    sx={{ maxWidth: "500px" }}
                                >
                                    <OutlinedInput
                                        id="email"
                                        data-testid="input-username"
                                        autoComplete="email"
                                        autoFocus={isLaptopScreen}
                                        placeholder="Contoh: ulo@gmail.com"
                                        size="small"
                                        {...field}
                                    />
                                    <FormHelperText>
                                        {errors.email
                                            ? errors.email.message
                                            : " "}
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2">Kata Sandi</Typography>
                        <Controller
                            name="password"
                            control={control}
                            render={({ field }) => (
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    error={Boolean(errors.password)}
                                    sx={{ maxWidth: "500px" }}
                                >
                                    <OutlinedInput
                                        id="password"
                                        data-testid="input-password"
                                        type={showPassword ? "text" : "password"}
                                        autoComplete="current-password"
                                        placeholder="Minimal 8 karakter"
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={
                                                        togglePasswordVisibility
                                                    }
                                                    edge="end"
                                                >
                                                    {showPassword ? (
                                                        <VisibilityOff />
                                                    ) : (
                                                        <Visibility />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                        size="small"
                                        {...field}
                                    />
                                    <FormHelperText>
                                        {errors.password
                                            ? errors.password.message
                                            : " "}
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />
                    </Grid>
                </Grid>
                <Box display="flex" justifyContent="flex-end">
                    <Link
                        component={RouterLink}
                        to="/lupa-kata-sandi"
                        variant="body2"
                        underline="hover"
                        sx={{ cursor: "pointer" }}
                    >
                        Lupa Kata Sandi?
                    </Link>
                </Box>
                <Box width="100%">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={saveEmail}
                                onChange={(_, checked) => {
                                    setSaveEmail(checked);
                                }}
                            />
                        }
                        label="Simpan Email ?"
                        sx={{ height: "fit-content" }}
                    />
                </Box>
                <Button
                    disabled={isLocked}
                    type="submit"
                    fullWidth
                    size="large"
                    variant="contained"
                    data-testid="button-masuk"
                    sx={{ marginTop: 3, maxWidth: "500px" }}
                >
                    {countdownLabel}
                </Button>
                <Button
                    fullWidth
                    size="large"
                    variant="outlined"
                    href={
                        process.env.NODE_ENV === "production"
                            ? "https://juragan.ukmlokal.com"
                            : "http://localhost:3000"
                    }
                    rel="noreferrer"
                    sx={{ marginTop: 2, maxWidth: "500px" }}
                    data-testid="button-juragan"
                >
                    Ke Juragan
                </Button>
                <Typography
                    variant="body2"
                    component="div"
                    textAlign="center"
                    sx={{ marginY: 2 }}
                >
                    Belum punya akun?{" "}
                    <Link
                        component={RouterLink}
                        to="/registrasi"
                        variant="body2"
                        underline="hover"
                        sx={{ cursor: "pointer" }}
                    >
                        Daftar
                    </Link>
                </Typography>
            </form>
        </Box>
    );
};

export default LoginForm;
